import {
  format,
  parseISO,
  isValid as isValidDate,
  parse,
  setHours,
  setMinutes,
  setSeconds,
  setMilliseconds,
  getDay,
  addDays
} from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { DEFAULT_TIME_FORMAT, type TimeFormatPreference } from '@/common/domain/constants/timeFormat';
import { organizationTimeFormatService } from '@/common/infrastructure/services/OrganizationTimeFormatService';

const TIME_PARSE_FORMATS = ['HH:mm:ss', 'HH:mm'];
const TIME_DISPLAY_FALLBACK = 'N/A';

type TimeFormatOptions = {
  timeFormat?: TimeFormatPreference;
  includeSeconds?: boolean;
  returnNullForInvalid?: boolean;
  fallback?: string;
};

type DateTimeFormatOptions = {
  timeFormat?: TimeFormatPreference;
  includeSeconds?: boolean;
  separator?: string;
};

const getEffectiveTimeFormat = (override?: TimeFormatPreference): TimeFormatPreference => {
  return override ?? organizationTimeFormatService.getOrganizationDefaultTimeFormat() ?? DEFAULT_TIME_FORMAT;
};

export const getTimeFormatPattern = (timeFormat: TimeFormatPreference, includeSeconds = false): string => {
  if (timeFormat === 'MilitaryTime') {
    return includeSeconds ? 'HH:mm:ss' : 'HH:mm';
  }
  return includeSeconds ? 'h:mm:ss a' : 'h:mm a';
};

const normalizeTimeString = (timeString: string): { hours: number; minutes: number; seconds: number } | null => {
  const trimmed = timeString.trim();
  if (!trimmed || trimmed === '0') return null;

  const match = /^(\d{1,2}):(\d{0,2})(?::(\d{1,2}))?$/.exec(trimmed);
  if (!match) return null;

  const [, hoursRaw, minutesRaw, secondsRaw] = match;
  if (!hoursRaw || hoursRaw.trim() === '' || hoursRaw === '0') return null;

  const hours = Number(hoursRaw);
  const minutes = minutesRaw === '' ? 0 : Number(minutesRaw);
  const seconds = secondsRaw ? Number(secondsRaw) : 0;

  if (Number.isNaN(hours) || Number.isNaN(minutes) || Number.isNaN(seconds)) return null;
  if (hours < 0 || hours > 23) return null;
  if (minutes < 0 || minutes > 59) return null;
  if (seconds < 0 || seconds > 59) return null;

  return { hours, minutes, seconds };
};

const formatInvalidTime = (options?: TimeFormatOptions): string | null => {
  if (options?.returnNullForInvalid) return null;
  return options?.fallback ?? TIME_DISPLAY_FALLBACK;
};

export const formatTimeForDisplay = (timeString: string | null | undefined, options?: TimeFormatOptions): string | null => {
  if (!timeString || typeof timeString !== 'string') {
    return formatInvalidTime(options);
  }

  const timeParts = normalizeTimeString(timeString);
  if (!timeParts) {
    return formatInvalidTime(options);
  }

  const { hours, minutes, seconds } = timeParts;
  const baseDate = setMilliseconds(setSeconds(setMinutes(setHours(new Date(), hours), minutes), seconds), 0);

  if (!isValidDate(baseDate)) {
    return formatInvalidTime(options);
  }

  const timeFormat = getEffectiveTimeFormat(options?.timeFormat);
  const pattern = getTimeFormatPattern(timeFormat, options?.includeSeconds);

  return format(baseDate, pattern);
};

export const formatTimeFromDate = (date: Date | undefined, options?: DateTimeFormatOptions): string | null => {
  if (!date) return null;
  try {
    const timeFormat = getEffectiveTimeFormat(options?.timeFormat);
    const pattern = getTimeFormatPattern(timeFormat, options?.includeSeconds);
    return format(date, pattern);
  } catch {
    return null;
  }
};

export const formatDateTimeForDisplay = (
  date: Date | string | undefined,
  dateFormat: string,
  options?: DateTimeFormatOptions
): string | null => {
  if (!date) return null;
  const timeFormat = getEffectiveTimeFormat(options?.timeFormat);
  const pattern = `${dateFormat}${options?.separator ?? ' '}${getTimeFormatPattern(timeFormat, options?.includeSeconds)}`;
  return formatDate(date, pattern) ?? null;
};

export const formatUTCDateTimeForDisplay = (
  date: Date | string | undefined,
  dateFormat: string,
  options?: DateTimeFormatOptions
): string | null => {
  if (!date) return null;
  const timeFormat = getEffectiveTimeFormat(options?.timeFormat);
  const pattern = `${dateFormat}${options?.separator ?? ' '}${getTimeFormatPattern(timeFormat, options?.includeSeconds)}`;
  return formatUTCDate(date, pattern) ?? null;
};

export const formatLocalDateTimeForDisplay = (
  date: Date | undefined,
  dateFormat: string,
  options?: DateTimeFormatOptions
): string | null => {
  if (!date) return null;
  try {
    const timeFormat = getEffectiveTimeFormat(options?.timeFormat);
    const pattern = `${dateFormat}${options?.separator ?? ' '}${getTimeFormatPattern(timeFormat, options?.includeSeconds)}`;
    return format(date, pattern);
  } catch {
    return null;
  }
};

/**
 * Converts a DateTime string or Date object to ISO 8601 time format
 * @param dateTime ISO DateTime string (with or without 'Z' suffix) or Date object
 * @returns ISO 8601 time format (HH:mm:ss)
 */
export const dateTimeToTimeString = (dateTime?: string | Date): string | undefined => {
  if (!dateTime) return undefined;

  // Handle Date objects
  if (dateTime instanceof Date) {
    if (!isValidDate(dateTime)) {
      return undefined;
    }
    // For Date objects, use local time with date-fns format
    return format(dateTime, 'HH:mm:ss');
  }

  // Handle string inputs
  if (typeof dateTime !== 'string') {
    return undefined;
  }

  try {
    const parsedDate = parseISO(dateTime);

    // Check if the date is valid
    if (!isValidDate(parsedDate)) {
      return undefined;
    }

    // If input has 'Z' suffix or timezone info (UTC), use UTC timezone; otherwise use local time
    // This preserves the timezone semantics of the input string
    const isUTC = dateTime.endsWith('Z') || dateTime.includes('+') || dateTime.includes('-', 10);

    if (isUTC) {
      // Format in UTC timezone
      return formatInTimeZone(parsedDate, 'UTC', 'HH:mm:ss');
    }

    // Format in local timezone
    return format(parsedDate, 'HH:mm:ss');
  } catch {
    return undefined;
  }
};

/**
 * Normalizes different time inputs (Date or string) to HH:mm:ss or null when invalid
 * @param timeField Date object or string representation of a time
 * @returns Time string (HH:mm:ss) or null
 */
export const normalizeTimeField = (timeField?: string | Date): string | null => {
  if (!timeField) return null;

  if (timeField instanceof Date) {
    return dateTimeToTimeString(timeField) ?? null;
  }

  const trimmed = timeField.trim();
  if (!trimmed) return null;

  const isoCandidate = dateTimeToTimeString(trimmed);
  if (isoCandidate) {
    return isoCandidate;
  }

  for (const formatString of TIME_PARSE_FORMATS) {
    try {
      const parsed = parse(trimmed, formatString, new Date());
      if (isValidDate(parsed)) {
        return format(parsed, 'HH:mm:ss');
      }
    } catch {
      continue;
    }
  }

  return null;
};

/**
 * Creates a DateTime string for a specific time on a given date
 * @param time ISO 8601 time format (HH:mm:ss)
 * @param date Base date to use
 * @returns ISO DateTime string
 */
export const createDateTimeForTime = (time: string, date: Date): string => {
  const [hours, minutes, seconds = '0'] = time.split(':');

  // Extract UTC date components from the base date using date-fns formatInTimeZone
  const year = parseInt(formatInTimeZone(date, 'UTC', 'yyyy'), 10);
  const month = parseInt(formatInTimeZone(date, 'UTC', 'MM'), 10) - 1; // date-fns months are 1-based, Date.UTC expects 0-based
  const day = parseInt(formatInTimeZone(date, 'UTC', 'dd'), 10);

  // Create a new UTC date - Date.UTC is needed to create UTC dates, but we format with date-fns
  const baseDate = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
  const newDate = setMilliseconds(setSeconds(setMinutes(setHours(baseDate, Number(hours)), Number(minutes)), Number(seconds)), 0);

  // Format as ISO string using date-fns
  return formatInTimeZone(newDate, 'UTC', "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
};

/**
 * Converts a time string to a Date object
 * @param time ISO 8601 time format (HH:mm:ss)
 * @returns Date object
 */
export const stringTimeToDate = (startTime: string | Date | undefined): Date | undefined => {
  if (!startTime) return undefined;

  if (startTime instanceof Date) return startTime;

  // Use date-fns to set time components in local timezone
  // Date.now() is used to get current timestamp (can be mocked in tests)
  const now = new Date(Date.now());
  const [hours, minutes, seconds] = startTime.split(':');

  // Set time components using date-fns (sets local time)
  const dateWithTime = setMilliseconds(
    setSeconds(setMinutes(setHours(now, parseInt(hours ?? '0')), parseInt(minutes ?? '0')), parseInt(seconds ?? '0')),
    0
  );
  return dateWithTime;
};

/**
 * Formats a date using date-fns format function
 * @param date Date to format
 * @param formatString Format string following date-fns format
 * @returns Formatted date string or undefined if date is invalid
 */
export const formatDate = (date: Date | string | undefined, formatString: string): string | undefined => {
  if (!date) return undefined;

  const dateObj = date instanceof Date ? date : parseISODate(date);

  if (dateObj && isNaN(dateObj.getTime())) {
    return undefined;
  }

  try {
    if (!dateObj) return undefined;

    // If format includes time (H, h, m, s), check if we should format in UTC
    const hasTimeFormat = /[Hhms]/.test(formatString);
    const isUTCString = typeof date === 'string' && (date.endsWith('Z') || date.includes('+') || date.includes('-', 10));

    // Format in UTC timezone if:
    // 1. Format includes time components AND
    // 2. Input was a UTC string (ends with Z or has timezone offset)
    if (hasTimeFormat && isUTCString) {
      return formatInTimeZone(dateObj, 'UTC', formatString);
    }

    // For Date objects with time components, format in UTC to avoid timezone shifts
    // This ensures Date objects created from UTC (like Date.UTC()) display correctly
    // For date-only formats (e.g., yyyy-MM-dd), use local time to preserve the date value
    if (date instanceof Date && hasTimeFormat) {
      return formatInTimeZone(dateObj, 'UTC', formatString);
    }

    return format(dateObj, formatString);
  } catch (error) {
    console.error('Error formatting date:', error);
    return undefined;
  }
};

/**
 * Parses a date string in ISO 8601 format to a Date object
 * @param dateString Date string in ISO 8601 format (e.g. '2024-03-21')
 * @returns Date object or undefined if invalid date string
 */
export const parseISODate = (dateString: string | undefined): Date | undefined => {
  if (!dateString) return undefined;

  try {
    const parsedDate = parseISO(dateString);
    if (isNaN(parsedDate.getTime())) throw new Error('Invalid date string');
    return parsedDate;
  } catch (error) {
    console.error('Error parsing ISO date:', error);
    return undefined;
  }
};

export const asDate = (raw: unknown): Date | null => {
  if (!raw) return null;
  if (raw instanceof Date) return isValidDate(raw) ? raw : null;
  if (typeof raw === 'string') {
    const try1 = parse(raw, 'yyyy-MM-dd', new Date());
    if (isValidDate(try1)) return try1;
    const try2 = parse(raw, 'MM/dd/yyyy', new Date());
    if (isValidDate(try2)) return try2;
    const try3 = new Date(raw);
    if (!Number.isNaN(try3.getTime())) return try3;
  }
  return null;
};

export const toDate = (raw: unknown): Date | null => {
  if (!raw) return null;
  if (raw instanceof Date) return isValidDate(raw) ? raw : null;
  if (typeof raw === 'string') {
    const try1 = parse(raw, 'yyyy-MM-dd', new Date()); // treats as local date
    if (isValidDate(try1)) return try1;
    const try2 = new Date(raw);
    if (isValidDate(try2)) return try2;
  }
  return null;
};

export function getWeekRangeUpdated(date: Date) {
  const day = getDay(date);
  const diffToMonday = (day === 0 ? -6 : 1) - day;
  const firstDay = addDays(date, diffToMonday);
  const lastDay = addDays(date, diffToMonday + 6);
  return { firstDay, lastDay };
}

export const formatSqlDateTime = (d: Date) => format(d, 'yyyy-MM-dd HH:mm:ss');

/**
 * Formats a UTC date safely (no timezone conversion)
 * Formats the date in UTC timezone without converting the actual time value
 * @param date Date or ISO string (assumed to be in UTC)
 * @param formatString Format string following date-fns patterns
 * @returns Formatted date string or undefined if invalid
 */
export const formatUTCDate = (date: Date | string | undefined, formatString: string): string | undefined => {
  if (!date) return undefined;

  try {
    // Ensure that it is a valid Date
    const dateObj = date instanceof Date ? date : parseISO(date);
    if (isNaN(dateObj.getTime())) return undefined;

    // Use formatInTimeZone to format the date in UTC timezone
    // This preserves the UTC time components without converting
    return formatInTimeZone(dateObj, 'UTC', formatString);
  } catch (error) {
    console.error('Error formatting UTC date:', error);
    return undefined;
  }
};

/**
 * Converts a Date to ISO 8601 string in local timezone (without 'Z' suffix)
 * This prevents automatic UTC conversion when sending to GraphQL
 * @param date Date object to convert
 * @returns ISO 8601 string in local timezone (e.g., '2024-01-15T00:00:00.000')
 */
export const toLocalISOString = (date: Date): string => {
  return format(date, "yyyy-MM-dd'T'HH:mm:ss.SSS");
};

/**
 * Converts a Date to UTC start of day (00:00:00.000) as Date object
 * Extracts year, month, day from the date and creates a UTC date at midnight
 * @param date Date object to convert (can be in any timezone)
 * @returns UTC Date object at start of day (e.g., Date representing '2025-11-27T00:00:00.000Z')
 */
export const toUTCStartOfDay = (date: Date): Date => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  return new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
};

/**
 * Converts a Date to UTC end of day (23:59:59.999) as Date object
 * Extracts year, month, day from the date and creates a UTC date at end of day
 * @param date Date object to convert (can be in any timezone)
 * @returns UTC Date object at end of day (e.g., Date representing '2025-11-27T23:59:59.999Z')
 */
export const toUTCEndOfDay = (date: Date): Date => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  return new Date(Date.UTC(year, month, day, 23, 59, 59, 999));
};

/**
 * Parses shorthand time input into a Date object
 * Supports formats like:
 * - "8p" or "8pm" → 8:00 PM
 * - "730a" or "730am" → 7:30 AM
 * - "12:30p" → 12:30 PM
 * - "9:15a" → 9:15 AM
 * @param input Shorthand time string
 * @param baseDate Base date to use for the time (defaults to today)
 * @returns Date object with parsed time, or null if invalid
 */
export const parseShorthandTime = (input: string, baseDate: Date = new Date()): Date | null => {
  if (!input || typeof input !== 'string') return null;

  // Trim and normalize input
  const normalized = input.trim().toLowerCase();

  // First, try to parse as standard time format (HH:mm AM/PM or H:mm AM/PM)
  try {
    const parsed = parse(normalized, 'h:mm a', baseDate);
    if (isValidDate(parsed)) return parsed;

    // Try without minutes
    const parsed2 = parse(normalized, 'h a', baseDate);
    if (isValidDate(parsed2)) return parsed2;
  } catch {
    // Continue to shorthand parsing
  }

  // Remove common separators and whitespace for shorthand parsing
  const cleaned = normalized.replace(/[\s:]/g, '');

  // Match shorthand patterns like: 8p, 730a, 1230pm
  // Pattern: 1-2 digits for hour, 0-2 digits for minutes, required a/p, optional m
  // We need to be smart about splitting hours and minutes
  // Examples: "8p" (hour=8), "730a" (hour=7, min=30), "1230p" (hour=12, min=30)
  const shorthandPattern = /^(\d{1,4})([ap])(m?)$/;
  const match = cleaned.match(shorthandPattern);

  if (!match) {
    return null;
  }

  const [, digits, ampm] = match;

  if (!digits || !ampm) {
    return null;
  }

  let hours: number;
  let minutes: number;

  // Determine hours and minutes based on digit count
  if (digits.length === 1 || digits.length === 2) {
    // 1-2 digits: just hours (e.g., "8p" or "12p")
    hours = parseInt(digits, 10);
    minutes = 0;
  } else if (digits.length === 3) {
    // 3 digits: first digit is hour, last 2 are minutes (e.g., "730a" = 7:30)
    hours = parseInt(digits[0] ?? '0', 10);
    minutes = parseInt(digits.slice(1) ?? '0', 10);
  } else if (digits.length === 4) {
    // 4 digits: first 2 are hour, last 2 are minutes (e.g., "1230p" = 12:30)
    hours = parseInt(digits.slice(0, 2) ?? '0', 10);
    minutes = parseInt(digits.slice(2) ?? '0', 10);
  } else {
    return null;
  }

  // Validate hours and minutes
  if (isNaN(hours) || hours < 1 || hours > 12) {
    return null;
  }
  if (isNaN(minutes) || minutes < 0 || minutes > 59) {
    return null;
  }

  // Handle hour format: 12-hour to 24-hour
  if (ampm === 'p' && hours !== 12) {
    hours += 12;
  } else if (ampm === 'a' && hours === 12) {
    hours = 0;
  }

  // Always create a fresh date with today's date to avoid stale data
  // Only set the time components, ignore any date/time from baseDate
  const freshDate = new Date();
  freshDate.setHours(hours, minutes, 0, 0);

  return isValidDate(freshDate) ? freshDate : null;
};

/**
 * Converts IANA timezone identifier (e.g., "Europe/London") to Windows timezone name (e.g., "GMT Standard Time")
 * If the timezone is already a Windows timezone name or not found in mapping, returns it as-is.
 * This is a fallback function for cases where the value might still be an IANA identifier.
 * @param timezone IANA identifier or Windows timezone name
 * @param timezoneMapping Mapping object from IANA to Windows timezone names (defaults to jobs timezone mapping)
 * @returns Windows timezone name
 */
export const convertToWindowsTimezone = (
  timezone: string | null | undefined,
  timezoneMapping?: Record<string, string>
): string | undefined => {
  if (!timezone) {
    return undefined;
  }

  // Check if it's already a Windows timezone name (contains spaces and "Standard Time" or "Daylight Time")
  // Or if it's UTC/GMT
  if (timezone.includes('Standard Time') || timezone.includes('Daylight Time') || timezone === 'UTC' || timezone === 'GMT') {
    return timezone;
  }

  // If mapping is provided, use it; otherwise, return timezone as-is
  // The mapping should be imported from timezoneMapping.json when needed
  if (timezoneMapping) {
    const mappedTimezone = timezoneMapping[timezone];
    return mappedTimezone || timezone;
  }

  // If no mapping provided, return as-is (assumes it's already a Windows timezone name)
  return timezone;
};

/**
 * Converts Windows timezone name (e.g., "E. South America Standard Time") to IANA timezone identifier (e.g., "America/Sao_Paulo")
 * This is needed because JavaScript's Intl API requires IANA identifiers, not Windows timezone names.
 * @param windowsTimezone Windows timezone name (e.g., "E. South America Standard Time")
 * @param timezoneMapping Mapping object from IANA to Windows timezone names (reverse lookup)
 * @returns IANA timezone identifier or undefined if not found
 */
export const convertWindowsToIanaTimezone = (
  windowsTimezone: string | null | undefined,
  timezoneMapping?: Record<string, string>
): string | undefined => {
  if (!windowsTimezone) {
    return undefined;
  }

  // Check if it's already an IANA identifier (contains "/" and doesn't contain "Standard Time" or "Daylight Time")
  if (!windowsTimezone.includes('Standard Time') && !windowsTimezone.includes('Daylight Time') && windowsTimezone.includes('/')) {
    // Already an IANA identifier
    return windowsTimezone;
  }

  // Handle special cases
  if (windowsTimezone === 'UTC') {
    return 'UTC';
  }
  if (windowsTimezone === 'GMT Standard Time' || windowsTimezone === 'GMT') {
    return 'Europe/London';
  }

  // Reverse lookup: find the IANA identifier that maps to this Windows timezone name
  if (timezoneMapping) {
    // Find the first key (IANA) that maps to this Windows timezone name
    const ianaTimezone = Object.keys(timezoneMapping).find((key) => timezoneMapping[key] === windowsTimezone);
    if (ianaTimezone) {
      return ianaTimezone;
    }
  }

  // If not found in mapping, return undefined to indicate invalid timezone
  // Avoid passing invalid timezone strings to date libraries / Intl
  return undefined;
};

/**
 * Timezone formatting utilities
 * Following Single Responsibility Principle - each function has one clear purpose
 */

/**
 * Fixed UTC reference time used for timezone offset calculation
 * Using a fixed date avoids DST variations and ensures consistent calculations
 */
const UTC_REFERENCE_TIME = new Date('2024-01-01T12:00:00Z');

/**
 * Gets the long timezone name (e.g., "Mountain Standard Time") from an IANA timezone ID
 * @param timezoneId IANA timezone identifier (e.g., "America/Boise")
 * @returns Long timezone name or the original ID if formatting fails
 */
export const getTimezoneLongName = (timezoneId: string): string => {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezoneId,
      timeZoneName: 'long'
    });
    const parts = formatter.formatToParts(new Date());
    const timeZoneName = parts.find((part) => part.type === 'timeZoneName')?.value;
    return timeZoneName || timezoneId;
  } catch {
    return timezoneId;
  }
};

/**
 * Parses a time string (HH:mm) into hours and minutes
 * @param timeString Time string in format "HH:mm"
 * @returns Object with hours and minutes, or { hours: 0, minutes: 0 } if invalid
 */
const parseTimeString = (timeString: string): { hours: number; minutes: number } => {
  const parts = timeString.split(':');
  const hours = parts[0] ? Number(parts[0]) : 0;
  const minutes = parts[1] ? Number(parts[1]) : 0;
  return { hours, minutes };
};

/**
 * Calculates the GMT offset in minutes for a given timezone
 * Uses a fixed UTC reference time to avoid DST variations
 * @param timezoneId IANA timezone identifier (e.g., "America/Boise")
 * @returns Offset in minutes (negative if timezone is behind UTC)
 */
export const calculateTimezoneOffset = (timezoneId: string): number => {
  try {
    const utcFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    const tzFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezoneId,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    const utcTime = utcFormatter.format(UTC_REFERENCE_TIME);
    const tzTime = tzFormatter.format(UTC_REFERENCE_TIME);

    const utcTimeParts = parseTimeString(utcTime);
    const tzTimeParts = parseTimeString(tzTime);

    const utcTotalMinutes = utcTimeParts.hours * 60 + utcTimeParts.minutes;
    const tzTotalMinutes = tzTimeParts.hours * 60 + tzTimeParts.minutes;

    // Calculate offset: timezone time - UTC time
    // If UTC is 12:00 and timezone is 05:00, offset is -420 minutes (-7 hours)
    let offsetMinutes = tzTotalMinutes - utcTotalMinutes;

    // Normalize to -12 to +12 hours range (handles day boundaries)
    if (offsetMinutes > 720) {
      offsetMinutes -= 1440; // Subtract 24 hours
    } else if (offsetMinutes < -720) {
      offsetMinutes += 1440; // Add 24 hours
    }

    return offsetMinutes;
  } catch {
    return 0;
  }
};

/**
 * Formats GMT offset as a string (e.g., "GMT-07:00" or "GMT+05:30")
 * @param offsetMinutes Offset in minutes (negative if timezone is behind UTC)
 * @returns Formatted GMT offset string
 */
export const formatGmtOffset = (offsetMinutes: number): string => {
  const offsetHours = Math.floor(offsetMinutes / 60);
  const offsetMins = Math.abs(offsetMinutes % 60);
  const offsetSign = offsetHours < 0 ? '-' : '+';
  const hoursString = Math.abs(offsetHours).toString().padStart(2, '0');
  const minutesString = offsetMins.toString().padStart(2, '0');

  return `GMT${offsetSign}${hoursString}:${minutesString}`;
};

/**
 * Extracts city name from IANA timezone identifier
 * @param timezoneId IANA timezone identifier (e.g., "America/Boise" or "Europe/London")
 * @returns City name with underscores replaced by spaces (e.g., "Boise" or "New York")
 */
export const extractCityNameFromTimezoneId = (timezoneId: string): string => {
  const cityName = timezoneId.split('/').pop()?.replace(/_/g, ' ') || '';
  return cityName;
};

/**
 * Formats timezone for display in the format: "(GMT-07:00) Mountain Standard Time - Boise"
 * @param timezoneId IANA timezone identifier (e.g., "America/Boise")
 * @returns Formatted timezone string or the original ID if formatting fails
 */
export const formatTimezoneDisplay = (timezoneId: string | null | undefined): string => {
  if (!timezoneId) {
    return 'N/A';
  }

  try {
    const timeZoneName = getTimezoneLongName(timezoneId);
    const offsetMinutes = calculateTimezoneOffset(timezoneId);
    const offsetFormatted = formatGmtOffset(offsetMinutes);
    const cityName = extractCityNameFromTimezoneId(timezoneId);

    return `(${offsetFormatted}) ${timeZoneName} - ${cityName}`;
  } catch {
    return timezoneId;
  }
};
